// src/components/NotificationList.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { Bell, Inbox, CheckCheck, Trash2, ShoppingBag, CheckCircle, XCircle, Info, AlertCircle, MessageSquare, Star, TrendingUp, MapPin } from "lucide-react";
import * as offersService from "../services/offersService";
import * as notificationsService from "../services/notificationsService";
import { userRepository } from "../repositories/userRepository";
import { productRepository } from "../repositories/productRepository";
import { errorDev } from '../utils/logger.js';
import {
  resolveNotificationColors,
  formatRelativeTime,
  normalizeNotificationType
} from "../config/notificationConfig";

// Caches for user and product data
const userCache = new Map();
const productCache = new Map();

// Hook to fetch user display name
function useUserName(uid) {
  const [name, setName] = useState(null);
  
  useEffect(() => {
    let mounted = true;
    
    async function load() {
      if (!uid) {
        setName(null);
        return;
      }
      
      if (userCache.has(uid)) {
        if (mounted) setName(userCache.get(uid));
        return;
      }
      
      try {
        // Route through userRepository so this component shares one
        // profile-read definition with the rest of the app instead of
        // building a Firestore document ref inline.
        const profile = await userRepository.getById(uid);
        const displayName = profile
          ? (profile.displayName || profile.name || profile.email?.split('@')[0] || null)
          : null;
        
        if (displayName) {
          userCache.set(uid, displayName);
        }
        if (mounted) setName(displayName);
      } catch (err) {
        errorDev("Operation failed:", err);
        if (mounted) setName(null);
      }
    }
    
    load();
    return () => { mounted = false; };
  }, [uid]);
  
  return name;
}

// Async helper to fetch product name with cache
async function getProductName(productId) {
  if (!productId) return null;
  if (productCache.has(productId)) return productCache.get(productId);
  
  try {
    // Route through productRepository so this component shares one
    // product-read definition with the rest of the app.
    const product = await productRepository.getById(productId);
    const name = product
      ? (product.title || product.name || null)
      : null;
    
    if (name) {
      productCache.set(productId, name);
    }
    return name;
  } catch (err) {
    errorDev("Operation failed:", err);
    return null;
  }
}

// Format currency amount
function formatAmount(amount, currency = 'RWF') {
  if (amount == null || isNaN(Number(amount))) return null;
  return `${Number(amount).toLocaleString()} ${currency}`;
}

function isOfferNotificationType(type) {
  const normalized = String(type || '').toLowerCase();
  return normalized === 'offer'
    || normalized === 'offer_received'
    || normalized === 'offer:accepted'
    || normalized === 'offer_accepted'
    || normalized === 'offer:declined'
    || normalized === 'offer_declined'
    || normalized === 'offer_rejected'
    || normalized === 'offer_update';
}

// Map notification types to icons - simple, no color needed
const NOTIFICATION_ICON_MAP = {
  'offer': ShoppingBag,
  'offer_received': ShoppingBag,
  'offer:accepted': CheckCircle,
  'offer_accepted': CheckCircle,
  'offer:declined': XCircle,
  'offer_declined': XCircle,
  'offer_rejected': XCircle,
  'offer_update': ShoppingBag,
  'system': Bell,
  'info': Info,
  'success': CheckCircle,
  'warning': AlertCircle,
  'error': XCircle,
  'new_message': MessageSquare,
  'rating_prompt': Star,
  'rating_received': Star,
  'points_earned': TrendingUp,
  'location_shared': MapPin,
};

function getNotificationIcon(type) {
  if (!type) return Bell;
  const normalized = String(type).toLowerCase();
  return NOTIFICATION_ICON_MAP[normalized] || Bell;
}

// Single notification item component
function NotificationItem({ notification, onClose, onMarkRead, onDelete }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["inbox", "common"]);
  const [enrichedText, setEnrichedText] = useState('');
  const [isEnriching, setIsEnriching] = useState(false);
  
  const fromUserId = notification.fromUserId || notification.payload?.fromUserId;
  const fromName = useUserName(fromUserId);
  
  const notificationType = normalizeNotificationType(notification.type);
  const colors = resolveNotificationColors(notification.type);
  const Icon = getNotificationIcon(notification.type);
  const isUnread = notificationsService.isNotificationUnread(notification);
  
  // Enrich notification text with additional context
  useEffect(() => {
    let mounted = true;
    
    async function enrich() {
      if (!notification) return;
      
      const baseText = notification.content || notification.message || notification.text || notification.body || '';
      
      // Check if this is an offer notification that needs enrichment
      const offerId = notification.payload?.offerId || notification.offerId;
      const isOfferType = notification.type?.toLowerCase().includes('offer');
      
      if (isOfferType && offerId) {
        setIsEnriching(true);
        try {
          const offer = await offersService.getOfferById(offerId);
          if (!offer || !mounted) {
            setEnrichedText(baseText);
            return;
          }
          
          const actorName = fromName || 'Someone';
          const productId = notification.payload?.productId || offer?.productId;
          const productName = await getProductName(productId);
          const amount = offer?.amount ?? notification.payload?.amount;
          const prettyAmount = formatAmount(amount);
          
          let text = baseText;
          
          if (notification.type === 'offer' || notification.type === 'offer_received') {
            text = `${actorName} sent an offer${prettyAmount ? ` of ${prettyAmount}` : ''}${productName ? ` for ${productName}` : ''}`;
          } else if (notification.type === 'offer:accepted' || notification.type === 'offer_accepted') {
            text = `${actorName} accepted your offer${prettyAmount ? ` of ${prettyAmount}` : ''}${productName ? ` for ${productName}` : ''}`;
          } else if (notification.type === 'offer:declined' || notification.type === 'offer_rejected') {
            text = `${actorName} declined your offer${prettyAmount ? ` of ${prettyAmount}` : ''}${productName ? ` for ${productName}` : ''}`;
          }
          
          if (mounted) setEnrichedText(text);
        } catch (err) {
          errorDev("Operation failed:", err);
          if (mounted) setEnrichedText(baseText);
        } finally {
          if (mounted) setIsEnriching(false);
        }
      } else {
        setEnrichedText(baseText);
      }
    }
    
    enrich();
    return () => { mounted = false; };
  }, [notification, fromName]);
  
  // Handle notification click - navigate to relevant page
  const handleClick = useCallback(async () => {
    // Mark as read
    if (isUnread && notification.id) {
      notificationsService.markNotificationRead(notification.id);
      onMarkRead?.(notification.id);
    }
    
    // Determine navigation target
    const payload = notification.payload || {};
    let target = null;
    let resolvedChatId = payload.chatId || payload.conversationId || payload?.context?.chatId || notification.chatId || null;

    if (!resolvedChatId && isOfferNotificationType(notification.type)) {
      const offerId = payload.offerId || notification.offerId || null;
      if (offerId) {
        try {
          const offer = await offersService.getOfferById(offerId);
          resolvedChatId = offer?.chatId || null;
        } catch (err) {
          errorDev("Operation failed:", err);
          resolvedChatId = null;
        }
      }
    }
    
    if (resolvedChatId) {
      target = `/messages/${encodeURIComponent(String(resolvedChatId))}`;
    } else if (payload.productId) {
      target = `/product/${payload.productId}`;
    } else if (notification.link) {
      target = notification.link;
    }
    
    if (target) {
      navigate(target);
      onClose?.();
    }
  }, [notification, isUnread, navigate, onClose, onMarkRead]);
  
  const title = notification.title || null;
  const timeAgo = formatRelativeTime(notification.createdAt);

  const handleDelete = useCallback(async (event) => {
    event.stopPropagation();
    if (!notification?.id) return;
    await notificationsService.dismissNotification(notification.id);
    onDelete?.(notification.id);
  }, [notification?.id, onDelete]);
  
  return (
    <li
      onClick={handleClick}
      className={`
        relative flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200
        ${isUnread 
          ? `${colors.bg} border ${colors.border}` 
          : 'hover:bg-gray-50 dark:hover:bg-neutral-800/60'
        }
      `}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && void handleClick()}
    >
      {/* Icon */}
      <div className={`
        flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center
        ${isUnread ? colors.iconBg : 'bg-gray-100 dark:bg-neutral-800'}
      `}>
        <Icon className={`w-4 h-4 ${isUnread ? colors.icon : 'text-gray-500 dark:text-gray-400'}`} />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <p className={`
            text-sm font-semibold truncate
            ${isUnread ? colors.text : 'text-gray-900 dark:text-gray-100'}
          `}>
            {title}
          </p>
        )}
        <p className={`
          text-sm leading-snug
          ${isUnread ? 'text-gray-700 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}
          ${isEnriching ? 'animate-pulse' : ''}
        `}>
          {enrichedText || t('inbox:loading', 'Loading...')}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
          {timeAgo}
        </p>
      </div>
      
      {/* Unread indicator */}
      {isUnread && (
        <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-green-500" />
      )}

      <button
        type="button"
        onClick={handleDelete}
        className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
        aria-label={t('inbox:deleteNotification', 'Delete notification')}
      >
        <Trash2 className="w-3 h-3" />
        {t('inbox:delete', 'Delete')}
      </button>
    </li>
  );
}

// Empty state component
function EmptyState({ isCompact }) {
  const { t } = useTranslation(["inbox", "common"]);

  return (
    <div className={`text-center ${isCompact ? 'py-8' : 'py-16'}`}>
      <div className={`
        mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4
        bg-gray-100 dark:bg-neutral-800
      `}>
        <Inbox className="w-8 h-8 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {t('inbox:emptyNotifications', 'No notifications yet')}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('inbox:emptyNotificationsDescription', "You'll be notified about offers, claims, and updates here.")}
      </p>
    </div>
  );
}

// Main NotificationList component
export default function NotificationList({ 
  notifications = [], 
  onClose,
  showHeader = false,
  isCompact = true,
  maxItems = null
}) {
  const { user } = useAuth();
  const [localNotifications, setLocalNotifications] = useState(
    (notifications || []).filter((n) => notificationsService.isNotificationSubscribed(n))
  );
  
  // Sync with prop changes
  useEffect(() => {
    setLocalNotifications((notifications || []).filter((n) => notificationsService.isNotificationSubscribed(n)));
  }, [notifications]);
  
  // Mark notification as read locally
  const handleMarkRead = useCallback((notificationId) => {
    setLocalNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, isRead: true, read: true } : n)
    );
  }, []);

  const handleDelete = useCallback((notificationId) => {
    setLocalNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);
  
  // Mark all as read
  const handleMarkAllRead = useCallback(async () => {
    if (!user) return;
    
    try {
      await notificationsService.markAllNotificationsRead(user.uid);
      setLocalNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true, read: true }))
      );
    } catch (err) {
      // Silently fail - user will see update on next sync
    errorDev("Operation failed:", err);
    }
  }, [user]);
  
  // Count unread notifications
  const unreadCount = useMemo(() => {
    return localNotifications.filter((n) => notificationsService.isNotificationUnread(n)).length;
  }, [localNotifications]);
  
  // Limit displayed items if maxItems is set
  const displayedNotifications = useMemo(() => {
    if (maxItems && localNotifications.length > maxItems) {
      return localNotifications.slice(0, maxItems);
    }
    return localNotifications;
  }, [localNotifications, maxItems]);
  
  if (!displayedNotifications || displayedNotifications.length === 0) {
    return (
      <div className={`
        ${!isCompact ? 'bg-white dark:bg-neutral-900 shadow-sm rounded-xl border border-gray-200 dark:border-neutral-800' : ''}
        ${!isCompact ? 'p-4' : 'p-2'}
      `}>
        {showHeader && (
          <div className="flex items-center gap-2 mb-4 px-2">
            <Bell className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Notifications
            </h2>
          </div>
        )}
        <EmptyState isCompact={isCompact} />
      </div>
    );
  }
  
  return (
    <div className={`
      ${!isCompact ? 'bg-white dark:bg-neutral-900 shadow-sm rounded-xl border border-gray-200 dark:border-neutral-800' : ''}
      ${!isCompact ? 'p-4' : ''}
    `}>
      {showHeader && (
        <div className="flex items-center justify-between mb-4 px-2">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Notifications
            </h2>
          </div>
          
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Mark all read</span>
            </button>
          )}
        </div>
      )}
      
      <ul className={`
        space-y-1
        ${isCompact ? 'max-h-80 overflow-y-auto' : ''}
      `}>
        {displayedNotifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClose={onClose}
            onMarkRead={handleMarkRead}
            onDelete={handleDelete}
          />
        ))}
      </ul>
    </div>
  );
}
